import React, { useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Range, getTrackBackground } from 'react-range';

type Props = {
  classes?: string;
  isAutoplay?: boolean;
  showControls?: boolean;
  controlsPosition?: string;
  isLoop?: boolean;
  src: string;
  onClick?: () => void;
  width?: number;
  height?: number;
  poster?: string;
  isLazyLoad?: boolean;
};

const STEP = 0.1;
const MIN = 0;
const MAX = 1;

const Video = forwardRef(
  (
    {
      classes = '',
      isAutoplay = false,
      showControls = true,
      controlsPosition = 'max-lg:bottom-0 max-lg:left-0 lg:bottom-0 lg:right-0',
      isLoop = true,
      src,
      width,
      height,
      poster,
      isLazyLoad = true
    }: Props,
    ref: React.Ref<HTMLDivElement>
  ) => {
    // States
    const [values, setValues] = React.useState([0]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isInView, setIsInView] = useState(!isLazyLoad);

    useEffect(() => {
      if (!isLazyLoad) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsInView(true);
              observer.disconnect();
            }
          });
        },
        { threshold: 0 }
      );

      if (videoRef.current) observer.observe(videoRef.current);

      return () => observer.disconnect();
    }, [isLazyLoad]);

    // Refs
    const videoRef = useRef(null);

    const togglePlay = () => {
      if (isPlaying) {
        pauseVideo();
      } else {
        playVideo();
      }
    };

    const playVideo = async () => {
      if (!isReady && !isLazyLoad) return;

      await videoRef.current.play();
    };

    const pauseVideo = () => {
      if (!isReady && !isLazyLoad) return;

      videoRef.current.pause();
    };

    const toggleVolume = () => {
      if (values > 0) {
        setValues([0]);
        videoRef.current.volume = 0;
      } else {
        setValues([100]);
        videoRef.current.muted = false;
        videoRef.current.volume = 1;
      }
    };

    const handleVolumeChange = (values: number[]) => {
      setValues(values);
      videoRef.current.muted = false;
      videoRef.current.volume = values[0];
    };

    const handleCanPlay = () => {
      setIsReady(true);
    };

    const handleOnPlay = () => {
      setIsPlaying(true);
    };

    const handleOnPause = () => {
      setIsPlaying(false);
    };

    // Expose close function to Accordion component
    useImperativeHandle(ref, () => {
      return {
        play() {
          playVideo();
        },

        pause() {
          pauseVideo();
        }
      };
    });

    let shadowClass = '';
    const split = controlsPosition.split(' ');
    for (const part of split) {
      if (part.includes('max-lg:top')) {
        shadowClass += ' bg-[linear-gradient(0deg,_rgba(0,_0,_0,_0)_0%,_rgba(0,_0,_0,_0.5)_100%)]';
      } else if (part.includes('max-lg:bottom')) {
        shadowClass += ' bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0)_0%,_rgba(0,_0,_0,_0.5)_100%)]';
      } else if (part.includes('lg:top')) {
        shadowClass += ' lg:bg-[linear-gradient(0deg,_rgba(0,_0,_0,_0)_0%,_rgba(0,_0,_0,_0.5)_100%)]';
      } else if (part.includes('lg:bottom')) {
        shadowClass += ' lg:bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0)_0%,_rgba(0,_0,_0,_0.5)_100%)]';
      }
    }

    return (
      <div className='video-wrapper overflow-hidden relative w-full h-full flex justify-center align-center group'>
        <video
          ref={videoRef}
          preload={isLazyLoad ? 'none' : 'auto'}
          poster={isLazyLoad && !isInView ? undefined : poster}
          className={`video-wrapper__video ${classes}`}
          autoPlay={isAutoplay && isInView}
          muted
          disableRemotePlayback
          playsInline
          controlsList='nodownload'
          disablePictureInPicture
          loop={isLoop}
          onPlay={handleOnPlay}
          onPause={handleOnPause}
          onCanPlay={handleCanPlay}
          width={width}
          height={height}
        >
          {isInView && (
            <source
              src={src}
              type='video/mp4'
            />
          )}
        </video>

        {showControls && (
          <div
            className={`absolute swiper-no-swiping ${controlsPosition} cursor-pointer z-20 w-full md:max-w-[220px] px-4 lg:px-8 py-4 ${shadowClass}`}
          >
            <div className='w-full flex items-center justify-between gap-x-8'>
              <div onClick={togglePlay}>
                {isPlaying ? (
                  <svg
                    width='16px'
                    height='16px'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <g
                      id='SVGRepo_bgCarrier'
                      strokeWidth='0'
                    />

                    <g
                      id='SVGRepo_tracerCarrier'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />

                    <g id='SVGRepo_iconCarrier'>
                      <path
                        fillRule='evenodd'
                        clip-rule='evenodd'
                        d='M5.163 3.819C5 4.139 5 4.559 5 5.4v13.2c0 .84 0 1.26.163 1.581a1.5 1.5 0 0 0 .656.655c.32.164.74.164 1.581.164h.2c.84 0 1.26 0 1.581-.163a1.5 1.5 0 0 0 .656-.656c.163-.32.163-.74.163-1.581V5.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.656C8.861 3 8.441 3 7.6 3h-.2c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656zm9 0C14 4.139 14 4.559 14 5.4v13.2c0 .84 0 1.26.164 1.581a1.5 1.5 0 0 0 .655.655c.32.164.74.164 1.581.164h.2c.84 0 1.26 0 1.581-.163a1.5 1.5 0 0 0 .655-.656c.164-.32.164-.74.164-1.581V5.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.656C17.861 3 17.441 3 16.6 3h-.2c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.655.656z'
                        fill='#fff'
                      />
                    </g>
                  </svg>
                ) : (
                  <svg
                    width='16px'
                    height='16px'
                    viewBox='-0.5 0 7 7'
                    version='1.1'
                    xmlns='http://www.w3.org/2000/svg'
                    xmlnsXlink='http://www.w3.org/1999/xlink'
                    fill='#fff'
                  >
                    <g
                      id='SVGRepo_bgCarrier'
                      strokeWidth='0'
                    />

                    <g
                      id='SVGRepo_tracerCarrier'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />

                    <g id='SVGRepo_iconCarrier'>
                      <g
                        id='Page-1'
                        stroke='none'
                        strokeWidth='1'
                        fill='none'
                        fillRule='evenodd'
                      >
                        <g
                          id='Dribbble-Light-Preview'
                          transform='translate(-347.000000, -3766.000000)'
                          fill='#fff'
                        >
                          <g
                            id='icons'
                            transform='translate(56.000000, 160.000000)'
                          >
                            <path
                              d='M296.494737,3608.57322 L292.500752,3606.14219 C291.83208,3605.73542 291,3606.25002 291,3607.06891 L291,3611.93095 C291,3612.7509 291.83208,3613.26444 292.500752,3612.85767 L296.494737,3610.42771 C297.168421,3610.01774 297.168421,3608.98319 296.494737,3608.57322'
                              id='play-[#1003]'
                            ></path>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                )}
              </div>

              <div className='flex items-center gap-x-4'>
                <div onClick={() => toggleVolume()}>
                  {values > 0 ? (
                    <svg
                      width='20px'
                      height='20px'
                      viewBox='0 -1.5 31 31'
                      version='1.1'
                      xmlns='http://www.w3.org/2000/svg'
                      xmlnsXlink='http://www.w3.org/1999/xlink'
                      xmlns:sketch='http://www.bohemiancoding.com/sketch/ns'
                      fill='#fff'
                    >
                      <g
                        id='SVGRepo_bgCarrier'
                        strokeWidth='0'
                      />

                      <g
                        id='SVGRepo_tracerCarrier'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />

                      <g id='SVGRepo_iconCarrier'>
                        <g
                          id='Page-1'
                          stroke='none'
                          strokeWidth='1'
                          fill='none'
                          fillRule='evenodd'
                          sketch:type='MSPage'
                        >
                          <g
                            id='Icon-Set-Filled'
                            sketch:type='MSLayerGroup'
                            transform='translate(-258.000000, -571.000000)'
                            fill='#fff'
                          >
                            <path
                              d='M277,571.015 L277,573.068 C282.872,574.199 287,578.988 287,585 C287,590.978 283,595.609 277,596.932 L277,598.986 C283.776,597.994 289,592.143 289,585 C289,577.857 283.776,572.006 277,571.015 L277,571.015 Z M272,573 L265,577.667 L265,592.333 L272,597 C273.104,597 274,596.104 274,595 L274,575 C274,573.896 273.104,573 272,573 L272,573 Z M283,585 C283,581.477 280.388,578.59 277,578.101 L277,580.101 C279.282,580.564 281,582.581 281,585 C281,587.419 279.282,589.436 277,589.899 L277,591.899 C280.388,591.41 283,588.523 283,585 L283,585 Z M258,581 L258,589 C258,590.104 258.896,591 260,591 L263,591 L263,579 L260,579 C258.896,579 258,579.896 258,581 L258,581 Z'
                              id='volume-full'
                              sketch:type='MSShapeGroup'
                            ></path>
                          </g>
                        </g>
                      </g>
                    </svg>
                  ) : (
                    <svg
                      width='20px'
                      height='20px'
                      viewBox='0 -3 30 30'
                      version='1.1'
                      xmlns='http://www.w3.org/2000/svg'
                      xmlnsXlink='http://www.w3.org/1999/xlink'
                      xmlns:sketch='http://www.bohemiancoding.com/sketch/ns'
                      fill='#fff'
                    >
                      <g
                        id='SVGRepo_bgCarrier'
                        strokeWidth='0'
                      />

                      <g
                        id='SVGRepo_tracerCarrier'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />

                      <g id='SVGRepo_iconCarrier'>
                        <g
                          id='Page-1'
                          stroke='none'
                          strokeWidth='1'
                          fill='none'
                          fillRule='evenodd'
                          sketch:type='MSPage'
                        >
                          <g
                            id='Icon-Set-Filled'
                            sketch:type='MSLayerGroup'
                            transform='translate(-311.000000, -573.000000)'
                            fill='#fff'
                          >
                            <path
                              d='M336.444,585 L340.617,580.827 C341.067,580.377 341.109,579.688 340.711,579.289 C340.312,578.891 339.623,578.933 339.173,579.383 L335,583.556 L330.827,579.383 C330.377,578.933 329.688,578.891 329.289,579.289 C328.891,579.688 328.933,580.377 329.383,580.827 L333.556,585 L329.383,589.173 C328.933,589.623 328.891,590.312 329.289,590.711 C329.688,591.109 330.377,591.067 330.827,590.617 L335,586.444 L339.173,590.617 C339.623,591.067 340.312,591.109 340.711,590.711 C341.109,590.312 341.067,589.623 340.617,589.173 L336.444,585 L336.444,585 Z M325,573 L318,577.667 L318,592.333 L325,597 C326.104,597 327,596.104 327,595 L327,575 C327,573.896 326.104,573 325,573 L325,573 Z M311,581 L311,589 C311,590.104 311.896,591 313,591 L316,591 L316,579 L313,579 C311.896,579 311,579.896 311,581 L311,581 Z'
                              id='volume-muted'
                              sketch:type='MSShapeGroup'
                            ></path>
                          </g>
                        </g>
                      </g>
                    </svg>
                  )}
                </div>

                <Range
                  values={values}
                  step={STEP}
                  min={MIN}
                  max={MAX}
                  onChange={(values) => handleVolumeChange(values)}
                  renderTrack={({ props, children }) => (
                    <div
                      onMouseDown={props.onMouseDown}
                      onTouchStart={props.onTouchStart}
                      style={{
                        ...props.style,
                        height: '12px',
                        display: 'flex',
                        width: '100%'
                      }}
                    >
                      <div
                        ref={props.ref}
                        style={{
                          height: '5px',
                          width: '65px',
                          borderRadius: '4px',
                          background: getTrackBackground({
                            values,
                            colors: ['#FFFFFF', '#666666'],
                            min: MIN,
                            max: MAX
                          }),
                          alignSelf: 'center'
                        }}
                      >
                        {children}
                      </div>
                    </div>
                  )}
                  renderThumb={({ props }) => (
                    <div
                      {...props}
                      key={props.key}
                      style={{
                        ...props.style,
                        height: '12px',
                        width: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#FFF'
                      }}
                    ></div>
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);
export default Video;
